import { Form, Input, Modal, Select, message } from "antd";
import { useEffect, useMemo, useState } from "react";
import { updateAccount } from "../../../../api/bankAccountApi";
import { getCurrencies } from "../../../../api/institutionApi";
import { BANK_OPTIONS } from "./bankAccountConstants";

export default function BankAccountEditModal({
  open,
  account,
  institutionName,
  onClose,
  onSuccess,
}) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);

  useEffect(() => {
    if (!open) return;

    let mounted = true;

    async function loadCurrencies() {
      try {
        setLoadingCurrencies(true);
        const data = await getCurrencies();
        if (!mounted) return;
        setCurrencies(Array.isArray(data) ? data : []);
      } catch (error) {
        message.error("Валютын жагсаалт ачааллахад алдаа гарлаа");
      } finally {
        if (mounted) {
          setLoadingCurrencies(false);
        }
      }
    }

    loadCurrencies();

    return () => {
      mounted = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !account) {
      form.resetFields();
      return;
    }

    form.setFieldsValue({
      bank: account?.bank || undefined,
      accountNumber: account?.accountNumber || "",
      currencyId: account?.currency?.id || account?.currencyId || undefined,
    });
  }, [open, account, form]);

  const currencyOptions = useMemo(() => {
    return (currencies || []).map((item) => ({
      label: item.code || item.name || String(item.id),
      value: item.id,
    }));
  }, [currencies]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      await updateAccount(account.id, {
        bank: values.bank ?? null,
        accountNumber: values.accountNumber?.replace(/\s+/g, "").trim() || null,
        currencyId: values.currencyId ?? null,
      });

      message.success("Данс амжилттай шинэчлэгдлээ");
      onSuccess?.();
      onClose?.();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Данс засахад алдаа гарлаа"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose?.();
  };

  return (
    <Modal
      title={`Данс засах${institutionName ? ` - ${institutionName}` : ""}`}
      open={open}
      onCancel={handleCancel}
      onOk={handleSubmit}
      okText="Хадгалах"
      cancelText="Болих"
      confirmLoading={submitting}
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        <Form.Item label="Байгууллага">
          <Input value={institutionName || ""} disabled />
        </Form.Item>

        <Form.Item
          label="Банк"
          name="bank"
          rules={[{ required: true, message: "Банк сонгоно уу" }]}
        >
          <Select
            showSearch
            options={BANK_OPTIONS}
            placeholder="Банк сонгох"
            optionFilterProp="label"
          />
        </Form.Item>

        <Form.Item
          label="Дансны дугаар"
          name="accountNumber"
          rules={[
            { required: true, message: "Дансны дугаар оруулна уу" },
            { min: 6, message: "Хэт богино байна" },
            { max: 50, message: "Хэт урт байна" },
          ]}
        >
          <Input
            placeholder="Жишээ: 1234567890"
            maxLength={50}
            onChange={(e) => {
              const cleaned = e.target.value.replace(/\s+/g, "");
              form.setFieldValue("accountNumber", cleaned);
            }}
          />
        </Form.Item>

        <Form.Item
          label="Валют"
          name="currencyId"
          rules={[{ required: true, message: "Валют сонгоно уу" }]}
        >
          <Select
            loading={loadingCurrencies}
            options={currencyOptions}
            placeholder="Валют сонгох"
            optionFilterProp="label"
            showSearch
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}